import { useEffect, useRef, useState } from "react";
import { Bot, Send, User, Phone, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const QUICK = [
  "Apa tugas Bawaslu?",
  "Bagaimana cara menyampaikan laporan?",
  "Apa itu pengawasan partisipatif?",
  "Bagaimana cara menghubungi Bawaslu?",
];
const CATS = ["Tentang Bawaslu", "Pengawasan Pemilu", "Pelaporan", "Pengawasan Partisipatif", "Informasi Publik", "Kontak Bawaslu"];

export default function TanyaBawaslu() {
  const [messages, setMessages] = useState([
    { role: "bot", text: "Halo! Saya asisten HARMONI Bawaslu. Silakan ajukan pertanyaan umum seputar Bawaslu, pengawasan pemilu, dan cara pelaporan. Anda juga bisa memilih pertanyaan cepat di bawah." },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("harmoni_chat_session");
    if (!s) { s = crypto.randomUUID(); localStorage.setItem("harmoni_chat_session", s); }
    return s;
  });
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, typing]);

  const patchLastBot = (patch) =>
    setMessages((m) => { const a = [...m]; a[a.length - 1] = { ...a[a.length - 1], ...patch }; return a; });

  const streamAI = async (q) => {
    setMessages((m) => [...m, { role: "bot", text: "", ai: true, streaming: true }]);
    const res = await fetch(`${API}/chatbot/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: q, session_id: sessionId }),
    });
    if (!res.ok || !res.body) throw new Error("stream gagal");
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "", full = "", errored = false;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const parts = buf.split("\n\n");
      buf = parts.pop();
      for (const p of parts) {
        const line = p.trim();
        if (!line.startsWith("data:")) continue;
        let data;
        try { data = JSON.parse(line.slice(5)); } catch { continue; }
        if (data.error) errored = true;
        if (data.token) { full += data.token; patchLastBot({ text: full }); }
      }
    }
    patchLastBot({ streaming: false, failed: errored || !full });
    if (errored || !full) {
      patchLastBot({ text: "Maaf, layanan AI sedang tidak tersedia. Silakan hubungi Humas Bawaslu Sultra untuk informasi lebih lanjut.", ai: false });
      setShowContact(true);
    }
  };

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || typing) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput(""); setTyping(true); setShowContact(false);
    try {
      const { data } = await api.post("/chatbot", { message: q, session_id: sessionId });
      if (data.found) {
        setTimeout(() => {
          setMessages((m) => [...m, { role: "bot", text: data.answer }]);
          setTyping(false);
        }, 400);
      } else {
        await streamAI(q);
        setTyping(false);
      }
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Maaf, layanan AI sedang tidak tersedia. Silakan hubungi Humas Bawaslu Sultra." }]);
      setShowContact(true);
      setTyping(false);
    }
  };

  return (
    <div className="bg-surface min-h-[calc(100vh-6rem)]">
      <div className="container-page py-8 grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-5">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-bold text-navy mb-3 text-sm">Kategori Pertanyaan</h3>
            <div className="flex flex-wrap gap-2">
              {CATS.map((c) => (
                <button key={c} onClick={() => send(c)} className="text-xs bg-surface hover:bg-navy hover:text-white text-slate-600 px-3 py-1.5 rounded-full transition-colors border border-slate-200">{c}</button>
              ))}
            </div>
          </div>
          <div className="bg-navy text-white rounded-2xl p-5">
            <Sparkles className="h-6 w-6 text-brandred mb-2" />
            <p className="text-sm text-slate-300">Didukung <strong className="text-white">AI (Gemini)</strong> untuk menjawab pertanyaan seputar Bawaslu &amp; penyelenggaraan pemilu, diperkuat basis pengetahuan resmi Bawaslu Sultra.</p>
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl flex flex-col overflow-hidden" data-testid="chatbot-window" style={{ height: "70vh" }}>
          <div className="bg-navy text-white px-5 py-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-brandred flex items-center justify-center"><Bot className="h-5 w-5" /></div>
            <div>
              <h2 className="font-bold">Tanya Bawaslu</h2>
              <p className="text-xs text-slate-300">Temukan jawaban cepat atas pertanyaan umum tentang Bawaslu.</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 hide-scrollbar" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-brandred text-white" : "bg-navy text-white"}`}>
                  {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                </div>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-brandred text-white rounded-tr-sm" : "bg-surface text-slate-700 rounded-tl-sm"}`}>
                  {m.ai && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-brandred bg-brandred/10 rounded-full px-2 py-0.5 mb-1.5">
                      <Sparkles className="h-3 w-3" /> Jawaban AI
                    </span>
                  )}
                  <span className="whitespace-pre-wrap">{m.text}{m.streaming && <span className="inline-block w-1.5 h-4 bg-brandred animate-pulse ml-0.5 align-middle" />}</span>
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex gap-2.5">
                <div className="h-8 w-8 rounded-full bg-navy text-white flex items-center justify-center"><Bot className="h-4 w-4" /></div>
                <div className="bg-surface rounded-2xl px-4 py-3 flex gap-1">
                  {[0, 1, 2].map((d) => <span key={d} className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}
                </div>
              </div>
            )}
            {showContact && (
              <div className="pl-11">
                <Link to="/bawaslu-terdekat"><Button size="sm" className="bg-navy text-white" data-testid="hubungi-humas-btn"><Phone className="h-4 w-4 mr-1.5" /> Hubungi Humas</Button></Link>
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 p-4">
            <div className="flex flex-wrap gap-2 mb-3">
              {QUICK.map((q) => (
                <button key={q} onClick={() => send(q)} data-testid="quick-question" className="text-xs bg-brandred/5 hover:bg-brandred/10 text-brandred px-3 py-1.5 rounded-full font-medium transition-colors">{q}</button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
              <Input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ketik pertanyaan Anda..." className="flex-1" />
              <Button type="submit" className="bg-brandred hover:bg-brandred-700 text-white" data-testid="chat-send"><Send className="h-4 w-4" /></Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
